import { AdminCrudPage, AdminCrudPageConfig } from '@/components/admin';

/**
 * NCB Slabs.
 *
 * This screen was dead in three separate ways, which is why NCB slabs were
 * reported as missing from the product form — there was nowhere else to
 * maintain them:
 *
 *  1. The endpoint was `/api/v1/products/ncb-slabs/`, but `api` already has
 *     `baseURL = '/policy/api/v1'`, so every request went to
 *     `/policy/api/v1/api/v1/...` and 404'd. Nothing ever loaded. Every other
 *     admin config here is correctly relative (`/products/groups/`).
 *  2. The columns read `year` and `percentage`; the serializer sends
 *     `claim_free_years` and `ncb_percentage`, so both were blank.
 *  3. The form posted `year`/`percentage`, which
 *     `NCBSlabCreateUpdateSerializer` does not accept — so creating a slab
 *     could not succeed even once the URL was right.
 *
 * `vehicle_category` uses the codes the backend documents on the field
 * (`2w`, `4w`, `cv`), not the wizard's `two_wheeler`/`private_car` vocabulary.
 */

const VEHICLE_CATEGORY_OPTIONS = [
  { value: '', label: 'All vehicle types' },
  { value: '2w', label: 'Two Wheeler' },
  { value: '4w', label: 'Four Wheeler' },
  { value: 'cv', label: 'Commercial Vehicle' },
];

const config: AdminCrudPageConfig<{ id: string; [key: string]: unknown }> = {
  title: 'NCB Slabs',
  apiEndpoint: '/products/ncb-slabs/',
  queryKey: 'ncbSlabs',
  // Widths turn on the fixed table layout (G2) `AdminCrudPage` now supports.
  columns: [
    { key: 'claim_free_years', label: 'Claim-free Years', width: '14%', sortable: true },
    {
      key: 'ncb_percentage',
      label: 'NCB %',
      width: '10%',
      render: (v: unknown) => (v == null || v === '' ? '-' : `${v}%`),
    },
    { key: 'product_name', label: 'Product', width: '22%' },
    { key: 'insurer_name', label: 'Insurer', width: '20%' },
    {
      key: 'vehicle_category',
      label: 'Vehicle Type',
      width: '14%',
      render: (v: unknown) =>
        VEHICLE_CATEGORY_OPTIONS.find((o) => o.value === v)?.label || (v as string) || 'All',
    },
    { key: 'is_active', label: 'Status', width: '10%', render: (v: unknown) => (v ? 'Active' : 'Inactive') },
  ],
  tableLayout: 'fixed',
  formFields: [
    { name: 'claim_free_years', label: 'Claim-free Years', type: 'number', required: true },
    { name: 'ncb_percentage', label: 'NCB Percentage', type: 'number', required: true },
    // Both FKs are nullable on the model: a slab can belong to a product, or to
    // an insurer as their default, so neither is marked required.
    { name: 'product', label: 'Product', type: 'select', apiEndpoint: '/products/', searchable: true },
    { name: 'insurer', label: 'Insurer', type: 'select', apiEndpoint: '/insurers/', searchable: true },
    {
      name: 'vehicle_category',
      label: 'Vehicle Type',
      type: 'select',
      options: VEHICLE_CATEGORY_OPTIONS,
    },
    { name: 'is_active', label: 'Active', type: 'boolean' },
  ],
  filters: [
    { key: 'vehicle_category', label: 'Vehicle Type', options: VEHICLE_CATEGORY_OPTIONS },
  ],
};

export function NcbSlabsPage() {
  return <AdminCrudPage config={config} />;
}
